import {
  CONTAINER_HEIGHT,
  CONTAINER_WIDTH,
  DIAGRAM_ELEMENTS_MARGIN,
  LOGO_HEIGHT,
  LOGO_WIDTH,
  MM_PER_INCH,
  PAGE_HEIGHT,
  PAGE_MARGIN,
  PAGE_WIDTH,
  paragraphFifth,
  paragraphFour,
  paragraphOne,
  paragraphThree,
  paragraphTwo,
  PIXELS_PER_INCH,
  RENDER_RETRY_COUNT,
  RENDER_RETRY_DELAY_MS,
  SPACE_IN_TEXT,
} from '~/modules/identification/forms/OwnershipStructure/constants.ts';
import { jsPDF } from 'jspdf';
import {
  TGenerateOwnershipStructurePdfParams,
  TNodeCardData,
  TOwnerDiagramNode,
  TTextSegment,
} from '~/modules/identification/forms/OwnershipStructure/types.ts';
import ReactDOM from 'react-dom/client';
import { buildTree } from '~/modules/identification/forms/OwnershipStructure/utils/utils.tsx';
import ReactFlow, { Node, ReactFlowProvider, Background } from 'reactflow';
import React from 'react';
import { NodeCard } from '~/modules/identification/forms/OwnershipStructure/components/Diagram/NodeCard.tsx';
import { theme } from '~/theme';
import { toPng } from 'html-to-image';
import logoSrc from '~/assets/logo.png';
import {
  buildFlowElements,
  getLayoutedElements,
  getTodayDate,
} from '~/modules/identification/forms/OwnershipStructure/utils/diagramUtils.tsx';
import { ThemeProvider } from '@mui/material/styles';

const waitForNodes = (
  container: HTMLElement,
  retries: number,
  delay: number
): Promise<HTMLElement> =>
  new Promise((resolve, reject) => {
    let lastTransform: string | null = null;
    let stableFrames = 0;
    const REQUIRED_STABLE_FRAMES = 2;

    const attempt = (left: number) => {
      const flowEl = container.querySelector('.react-flow') as HTMLElement | null;
      const firstNode = container.querySelector('.react-flow__node') as HTMLElement | null;
      const viewportEl = container.querySelector('.react-flow__viewport') as HTMLElement | null;

      const hasSize = firstNode ? firstNode.getBoundingClientRect().width > 0 : false;
      const currentTransform = viewportEl?.style.transform || null;

      const isReady =
        flowEl &&
        firstNode &&
        hasSize &&
        viewportEl &&
        currentTransform &&
        currentTransform !== 'translate(0px, 0px) scale(1)';

      if (isReady) {
        if (currentTransform === lastTransform) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
          lastTransform = currentTransform;
        }
        if (stableFrames >= REQUIRED_STABLE_FRAMES) {
          resolve(flowEl as HTMLElement);
          return;
        }
      }

      if (left <= 0) {
        reject(
          new Error(
            `Diagram not ready. flowEl: ${!!flowEl}, hasNodes: ${!!firstNode}, hasSize: ${hasSize}, transform: ${currentTransform}`
          )
        );
      } else {
        requestAnimationFrame(() => setTimeout(() => attempt(left - 1), delay));
      }
    };

    requestAnimationFrame(() => attempt(retries));
  });

const createOffscreenContainer = (): HTMLElement => {
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: ${CONTAINER_WIDTH}px;
    height: ${CONTAINER_HEIGHT}px;
    z-index: -9999;
    opacity: 0;         
    pointer-events: none;
    overflow: hidden;
  `;
  document.body.appendChild(container);
  return container;
};

const parseTextSegments = (text: string): TTextSegment[] => {
  const segments: TTextSegment[] = [];
  const parts = text.split(/(\*\*.*?\*\*)/g);
  for (const part of parts) {
    if (part.startsWith('**') && part.endsWith('**')) {
      segments.push({ text: part.slice(2, -2), bold: true });
    } else if (part) {
      segments.push({ text: part, bold: false });
    }
  }
  return segments;
};

const drawCyrillicText = (
  pdf: jsPDF,
  segments: TTextSegment[],
  x: number,
  y: number,
  options: {
    fontSize?: number;
    fontWeight?: string | number;
    align?: 'left' | 'center' | 'right' | 'justify';
    maxWidth?: number;
  } = {}
): number => {
  const { fontSize = 10, fontWeight = 'normal', align = 'left', maxWidth } = options;
  const scale = 4;
  const fontPx = fontSize * scale;

  type Word = { text: string; weight: string | number };

  const allWords: Word[] = segments.flatMap(({ text, bold }) =>
    text
      .split(' ')
      .filter(Boolean)
      .map((w) => ({ text: w, weight: bold ? 'bold' : fontWeight }))
  );

  const measureWord = (word: Word): number => {
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d')!;
    ctx.font = `${word.weight} ${fontPx}px Tahoma`;
    return ctx.measureText(word.text).width;
  };

  const pxToMm = (px: number) => (px / scale / PIXELS_PER_INCH) * MM_PER_INCH;
  const lineHeightMm = pxToMm(fontPx * 1.5);

  const getLines = (): Word[][] => {
    if (!maxWidth) return [allWords];

    const maxWidthPx = (maxWidth / MM_PER_INCH) * PIXELS_PER_INCH * scale;
    const lines: Word[][] = [];
    let currentLine: Word[] = [];
    let currentWidth = 0;
    const spaceWidth = (() => {
      const c = document.createElement('canvas');
      const ctx = c.getContext('2d')!;
      ctx.font = `${fontPx}px Tahoma`;
      return ctx.measureText(' ').width;
    })();

    for (const word of allWords) {
      const wordWidth = measureWord(word);
      const addWidth = currentLine.length > 0 ? spaceWidth + wordWidth : wordWidth;

      if (currentWidth + addWidth > maxWidthPx && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = [word];
        currentWidth = wordWidth;
      } else {
        currentLine.push(word);
        currentWidth += addWidth;
      }
    }
    if (currentLine.length > 0) lines.push(currentLine);
    return lines;
  };

  const lines = getLines();

  const renderWord = (word: Word, curX: number, lineY: number) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.font = `${word.weight} ${fontPx}px Tahoma`;
    const wordWidthPx = measureWord(word);
    canvas.width = wordWidthPx + 4;
    canvas.height = fontPx * 1.4;
    ctx.font = `${word.weight} ${fontPx}px Tahoma`;
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'top';
    ctx.fillText(word.text, 2, 2);
    const wMm = pxToMm(canvas.width);
    const hMm = pxToMm(canvas.height);
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', curX, lineY, wMm, hMm);
    return wMm;
  };

  lines.forEach((lineWords, i) => {
    const isLastLine = i === lines.length - 1;
    const lineY = y + i * lineHeightMm;

    if (align === 'justify' && maxWidth && !isLastLine && lineWords.length > 1) {
      const totalWordsWidthPx = lineWords.reduce((sum, w) => sum + measureWord(w), 0);
      const maxWidthPx = (maxWidth / MM_PER_INCH) * PIXELS_PER_INCH * scale;
      const spaceWidthPx = (maxWidthPx - totalWordsWidthPx) / (lineWords.length - 1);

      let curX = x;
      lineWords.forEach((word) => {
        const wordWidthPx = measureWord(word);
        renderWord(word, curX, lineY);
        curX += pxToMm(wordWidthPx + spaceWidthPx);
      });
    } else {
      const totalWidthPx = lineWords.reduce((sum, w, idx) => {
        const space = (() => {
          const c = document.createElement('canvas');
          const ctx = c.getContext('2d')!;
          ctx.font = `${fontPx}px Tahoma`;
          return ctx.measureText(' ').width;
        })();
        return sum + measureWord(w) + (idx > 0 ? space : 0);
      }, 0);
      const totalWidthMm = pxToMm(totalWidthPx);

      let curX = x;
      if (align === 'center') curX = x - totalWidthMm / 2;
      if (align === 'right') curX = x - totalWidthMm;

      const spaceWidthMm = pxToMm(
        (() => {
          const c = document.createElement('canvas');
          const ctx = c.getContext('2d')!;
          ctx.font = `${fontPx}px Tahoma`;
          return ctx.measureText(' ').width;
        })()
      );

      lineWords.forEach((word, idx) => {
        if (idx > 0) curX += spaceWidthMm;
        const wMm = renderWord(word, curX, lineY);
        curX += wMm - pxToMm(4);
      });
    }
  });

  return lines.length * lineHeightMm;
};

export const generateOwnershipStructurePdf = ({
  owners,
  link,
  fullNameUkr,
  identificationNumber,
  clientFullInformation,
}: TGenerateOwnershipStructurePdfParams): Promise<File> => {
  return new Promise((resolve, reject) => {
    const container = createOffscreenContainer();
    const root = ReactDOM.createRoot(container);

    const cleanup = () => {
      try {
        root.unmount();
      } catch (_) {}

      try {
        document.body.removeChild(container);
      } catch (_) {}
    };

    const tree = buildTree(owners, link) as TOwnerDiagramNode[];
    const [builtNodes, builtEdges] = buildFlowElements(
      tree,
      fullNameUkr,
      identificationNumber,
      link
    );
    const [layoutedNodes, layoutedEdges] = getLayoutedElements(builtNodes, builtEdges);

    const nodesWithLabels = (layoutedNodes as Node[]).map((node) => ({
      ...node,
      data: {
        ...node.data,
        label: React.createElement(NodeCard, {
          data: node.data as TNodeCardData,
        }),
      },
    }));

    const DiagramComponent = () =>
      React.createElement(ThemeProvider, {
        theme,
        children: React.createElement(
          ReactFlowProvider,
          null,
          React.createElement(
            'div',
            {
              style: {
                width: CONTAINER_WIDTH,
                height: CONTAINER_HEIGHT,
              },
            },
            React.createElement(
              ReactFlow,
              {
                nodes: nodesWithLabels,
                edges: layoutedEdges,
                nodesDraggable: false,
                nodesConnectable: false,
                fitView: true,
                minZoom: 0.05,
                maxZoom: 2,
                onInit: (instance) => {
                  requestAnimationFrame(() => {
                    instance.fitView({ padding: 0.1, duration: 0 });
                  });
                },
              },
              React.createElement(Background)
            )
          )
        ),
      });

    root.render(React.createElement(DiagramComponent));

    waitForNodes(container, RENDER_RETRY_COUNT, RENDER_RETRY_DELAY_MS)
      .then(async (flowEl) => {
        try {
          const pngDataUrl = await toPng(flowEl, {
            cacheBust: true,
            pixelRatio: 2,
            filter: (node) => {
              if (node?.classList?.contains('react-flow__controls')) return false;
              if (node?.classList?.contains('react-flow__background')) return false;
              if (node?.classList?.contains('react-flow__attribution')) return false;
              return true;
            },
          });

          const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4',
            compress: true,
          });

          drawCyrillicText(
            pdf,
            [{ text: 'Додаток 1 до Анкети-опитувальника', bold: false }],
            PAGE_WIDTH - PAGE_MARGIN - DIAGRAM_ELEMENTS_MARGIN,
            PAGE_MARGIN + DIAGRAM_ELEMENTS_MARGIN,
            {
              fontSize: 14,
              fontWeight: 500,
              align: 'right',
            }
          );

          const titleY = PAGE_MARGIN + DIAGRAM_ELEMENTS_MARGIN + 10;
          const titleHeight = drawCyrillicText(
            pdf,
            [
              {
                text: 'Схематичне зображення структури власності юридичної особи – резидента',
                bold: true,
              },
            ],
            PAGE_WIDTH / 2,
            titleY,
            {
              fontSize: 14,
              fontWeight: 'bold',
              align: 'center',
              maxWidth: PAGE_WIDTH - PAGE_MARGIN + DIAGRAM_ELEMENTS_MARGIN * 2,
            }
          );

          // лого
          pdf.addImage(
            logoSrc,
            'PNG',
            PAGE_MARGIN + 4 + DIAGRAM_ELEMENTS_MARGIN,
            PAGE_MARGIN - 4 + DIAGRAM_ELEMENTS_MARGIN,
            LOGO_WIDTH,
            LOGO_HEIGHT
          );

          const TITLE_TO_DIAGRAM_GAP = 6;
          const BOTTOM_SAFE_MARGIN = PAGE_MARGIN;

          const diagramTop = titleY + titleHeight + TITLE_TO_DIAGRAM_GAP;

          const availableWidth = PAGE_WIDTH - PAGE_MARGIN * 2;
          const availableHeight = PAGE_HEIGHT - diagramTop - BOTTOM_SAFE_MARGIN;

          const scale = Math.min(
            availableWidth / CONTAINER_WIDTH,
            availableHeight / CONTAINER_HEIGHT
          );

          const renderedWidth = CONTAINER_WIDTH * scale;
          const renderedHeight = CONTAINER_HEIGHT * scale;
          const offsetX = (PAGE_WIDTH - renderedWidth) / 2;
          const offsetY = diagramTop;

          // діаграма
          pdf.addImage(pngDataUrl, 'PNG', offsetX, offsetY, renderedWidth, renderedHeight);

          pdf.addPage();

          const textMargin = 15;
          const paragraphGap = 1;
          const contentWidth = PAGE_WIDTH - textMargin * 2;

          const drawText = (
            text: string,
            y: number,
            options?: Parameters<typeof drawCyrillicText>[4] & { indent?: boolean }
          ) => {
            const { indent, ...rest } = options ?? {};
            const indentStr = indent ? SPACE_IN_TEXT : '';
            const segments = parseTextSegments(indentStr + text);

            return drawCyrillicText(pdf, segments, textMargin, y, {
              fontSize: 12,
              align: 'justify',
              maxWidth: contentWidth,
              ...rest,
            });
          };

          let y = textMargin;

          y += drawText(paragraphOne, y, { indent: true });
          y += paragraphGap;

          y += drawText(paragraphTwo, y, { indent: true });
          y += paragraphGap;

          y += drawText(paragraphThree, y, { indent: true });
          y += paragraphGap;

          y += drawText(paragraphFour, y, { indent: true });
          y += paragraphGap;

          y += drawText(paragraphFifth, y, { indent: true });

          drawCyrillicText(
            pdf,
            [
              {
                text: `Клієнт / Представник Клієнта ${SPACE_IN_TEXT} ${clientFullInformation}`,
                bold: false,
              },
            ],
            textMargin,
            PAGE_MARGIN + 65,
            {
              fontSize: 12,
              fontWeight: 'normal',
              align: 'left',
            }
          );
          drawCyrillicText(
            pdf,
            [{ text: '(ПІБ, підпис, відбиток печатки за наявності)', bold: false }],
            PAGE_WIDTH - textMargin,
            PAGE_MARGIN + 65,
            {
              fontSize: 12,
              fontWeight: 'normal',
              align: 'right',
            }
          );
          drawCyrillicText(
            pdf,
            [{ text: `${getTodayDate()} року`, bold: false }],
            PAGE_WIDTH - textMargin,
            PAGE_MARGIN + 70,
            {
              fontSize: 12,
              fontWeight: 'normal',
              align: 'right',
            }
          );

          const pdfBlob = pdf.output('blob');
          const file = new File([pdfBlob], 'ownership_structure.pdf', {
            type: 'application/pdf',
          });

          cleanup();
          resolve(file);
        } catch (error) {
          cleanup();
          reject(error);
        }
      })
      .catch((error) => {
        cleanup();
        reject(error);
      });
  });
};
